import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CalendarDays, CalendarHeart, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PageHeader, KPIGrid, KPICard, EmptyState } from "@/components/app/PageKit";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Holidays() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", type: "public" });

  const load = async () => {
    const r = await api.get("/holidays", { params: { year } });
    setHolidays(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const create = async () => {
    try { await api.post("/holidays", form); toast.success("Holiday added"); setOpen(false); setForm({date:"",name:"",type:"public"}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try { await api.delete(`/holidays/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const byMonth = {};
  MONTHS.forEach((_, i) => byMonth[i] = []);
  holidays.forEach((h) => {
    const m = new Date(h.date).getMonth();
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(h);
  });

  const counts = {
    public: holidays.filter(h => h.type === "public").length,
    optional: holidays.filter(h => h.type === "optional").length,
    company: holidays.filter(h => h.type === "company").length,
  };

  // Upcoming: next holiday >= today
  const upcoming = (() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const future = holidays
      .map(h => ({ ...h, _d: new Date(h.date) }))
      .filter(h => h._d >= today)
      .sort((a, b) => a._d - b._d);
    return future[0] || null;
  })();

  const typeBadge = (t) => {
    const cfg = { public: "bg-rose-100 text-rose-700", optional: "bg-amber-100 text-amber-700", company: "bg-cyan-100 text-cyan-700" };
    return <Badge className={`${cfg[t] || "bg-slate-100 text-slate-700"} border-0 capitalize text-[10px] font-bold`}>{t}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="holidays-page">
      <PageHeader
        category="Calendar"
        title="Holidays"
        description={`${holidays.length} holidays in ${year} · public, company and optional days off`}
        primaryAction={canEdit ? {
          label: "Add Holiday",
          icon: Plus,
          onClick: () => setOpen(true),
          testid: "new-holiday-btn",
        } : undefined}
        secondaryAction={{
          label: String(year),
          icon: CalendarDays,
          onClick: () => {},
          testid: "year-display",
        }}
      />

      {/* Year switcher */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Year</span>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32 h-9 bg-white" data-testid="holiday-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027, 2028].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPI strip */}
      <KPIGrid cols={4}>
        <KPICard icon={CalendarDays}  tone="violet"  label="Total Holidays" value={holidays.length} sub={`in ${year}`} testid="kpi-total"/>
        <KPICard icon={CalendarHeart} tone="rose"    label="Public"         value={counts.public} testid="kpi-public"/>
        <KPICard icon={CalendarRange} tone="amber"   label="Optional"       value={counts.optional} testid="kpi-optional"/>
        <KPICard icon={CalendarDays}  tone="cyan"    label="Company"        value={counts.company} testid="kpi-company"/>
      </KPIGrid>

      {/* Next up */}
      {upcoming && (
        <Card className="p-4 sm:p-5 border-violet-200 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-orange-50 shadow-sm" data-testid="next-holiday">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-white border border-violet-200 flex flex-col items-center justify-center shrink-0 shadow-sm">
              <span className="text-[10px] uppercase tracking-wider font-bold text-violet-600">{MONTHS[new Date(upcoming.date).getMonth()]}</span>
              <span className="text-xl font-bold font-[Outfit] text-slate-900 leading-none">{new Date(upcoming.date).getDate()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Next Up</div>
              <div className="text-base sm:text-lg font-bold font-[Outfit] text-slate-900 truncate">{upcoming.name}</div>
              <div className="text-[12px] text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                {typeBadge(upcoming.type)}
                <span>{new Date(upcoming.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Calendar grid */}
      {holidays.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No holidays added yet"
          message={`Set up ${year}'s holiday calendar so leave balances and payroll math stay accurate.`}
          action={canEdit ? { label: "Add First Holiday", icon: Plus, onClick: () => setOpen(true) } : undefined}
          testid="holidays-empty"
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {MONTHS.map((m, i) => (
            <Card key={m} className="p-3 border-slate-200 shadow-sm bg-white min-w-0" data-testid={`month-${m.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-[Outfit] font-bold text-slate-900">{m}</div>
                <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-slate-50">{byMonth[i]?.length || 0}</Badge>
              </div>
              {(!byMonth[i] || byMonth[i].length === 0) ? (
                <div className="text-[10px] text-slate-400 py-2 text-center">No holidays</div>
              ) : (
                <div className="space-y-1.5">
                  {byMonth[i].map((h) => (
                    <div key={h.id} className="p-2 rounded-lg bg-slate-50 border border-slate-100 flex items-center gap-2 group hover:border-violet-200 hover:bg-violet-50/40 transition" data-testid={`holiday-${h.id}`}>
                      <div className="w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0">
                        <span className="text-[11px] font-[Outfit] font-bold text-slate-900">{new Date(h.date).getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-slate-900 truncate" title={h.name}>{h.name}</div>
                        <div className="mt-0.5">{typeBadge(h.type)}</div>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => del(h.id)}
                          className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition shrink-0"
                          data-testid={`del-holiday-${h.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-holiday-dialog">
          <DialogHeader><DialogTitle>New Holiday</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1" data-testid="holiday-date-input"/></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="holiday-name-input"/></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                <SelectTrigger className="mt-1" data-testid="holiday-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public Holiday</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                  <SelectItem value="company">Company Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} className="qh-grad-bg text-white font-semibold" data-testid="holiday-save-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
